import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Shared active dimension unit across all facet form instances.
 * Persists through DOM morphing since it lives in JS memory.
 * Null until explicitly set — on first load, detected from server-rendered state.
 * @type {string | null}
 */
let activeDimensionUnit = null;

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  get activeUnit() {
    return activeDimensionUnit;
  }

  set activeUnit(unit) {
    activeDimensionUnit = unit;
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    // These controls sit in the facets form for layout, but they are view state, not filters.
    newParameters.delete('grid');
    newParameters.delete('grid-mobile');
    newParameters.delete('media-view');

    // Collapse the duplicate sort_by the select + radios both submit on a non-sort change.
    const sortValues = newParameters.getAll('sort_by');
    if (sortValues.length > 1) newParameters.set('sort_by', sortValues[0]);

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    // Preserve facet params this form has no control for, so a split-form submit keeps the sibling form's selections.
    const ownedNames = new Set();
    for (const element of this.refs.facetsForm.elements) {
      const name = element.getAttribute('name');
      if (name) ownedNames.add(name);
    }

    /** @param {string} name */
    const isFacetParam = (name) => name === 'sort_by' || name.startsWith('filter.');

    const preserved = [];
    for (const [param, value] of url.searchParams) {
      if (isFacetParam(param) && !ownedNames.has(param)) preserved.push([param, value]);
    }

    url.search = '';
    for (const [param, value] of preserved) url.searchParams.append(param, value);
    for (const [param, value] of urlParameters.entries()) url.searchParams.append(param, value);

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  #scrollFadeAbortController = new AbortController();

  /** @type {ResizeObserver | null} */
  #scrollFadeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initScrollFade();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollFadeAbortController.abort();
    this.#scrollFadeObserver?.disconnect();
  }

  /**
   * Flags the scroll wrapper when more items sit below the fold so CSS can fade the bottom edge.
   */
  #initScrollFade() {
    const wrapper = this.querySelector('.facets__inputs-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    const update = () => {
      const canScrollDown = wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight > 1;
      wrapper.toggleAttribute('data-can-scroll-down', canScrollDown);
    };

    wrapper.addEventListener('scroll', update, { signal: this.#scrollFadeAbortController.signal, passive: true });
    this.#scrollFadeObserver = new ResizeObserver(update);
    this.#scrollFadeObserver.observe(wrapper);
  }

  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement} [minRange] - The minimum price range slider thumb (optional)
 * @property {HTMLInputElement} [maxRange] - The maximum price range slider thumb (optional)
 * @property {HTMLElement} [sliderWrapper] - The range slider wrapper (optional)
 * @property {HTMLElement} [sliderTrack] - The gradient track behind the slider (optional)
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {number} */
  #rangeMin = 0;
  /** @type {number} */
  #rangeMax = 0;
  /** @type {number} */
  #step = 1;
  /** @type {number} */
  #gap = 1;
  /** @type {boolean} */
  #sliderInitialized = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.#initSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);

    const { minRange, maxRange, minInput, maxInput } = this.refs;
    if (minRange) {
      minRange.removeEventListener('input', this.#onMinRangeInput);
      minRange.removeEventListener('change', this.#onRangeChange);
    }
    if (maxRange) {
      maxRange.removeEventListener('input', this.#onMaxRangeInput);
      maxRange.removeEventListener('change', this.#onRangeChange);
    }
    if (minInput) minInput.removeEventListener('input', this.#onMinInputType);
    if (maxInput) maxInput.removeEventListener('input', this.#onMaxInputType);
  }

  /**
   * Initialises the optional dual-thumb slider. No-op when the slider markup
   * isn't present (theme setting `enable_price_slider` is off).
   */
  #initSlider() {
    const { minRange, maxRange, sliderWrapper, minInput, maxInput } = this.refs;
    if (!minRange || !maxRange || !sliderWrapper) return;
    if (this.#sliderInitialized) return;
    this.#sliderInitialized = true;

    this.#rangeMin = Number(sliderWrapper.getAttribute('data-range-min')) || 0;
    this.#rangeMax = Number(sliderWrapper.getAttribute('data-range-max')) || 0;

    // Disabled state: collection has no usable price spread.
    if (this.#rangeMax <= this.#rangeMin) {
      minRange.setAttribute('disabled', '');
      maxRange.setAttribute('disabled', '');
      this.#paintTrack(this.#rangeMin, this.#rangeMax);
      return;
    }

    // Adaptive step + gap, derived once at mount.
    this.#step = Math.max(1, Math.ceil(this.#rangeMax / 100));
    this.#gap = this.#step;

    for (const thumb of [minRange, maxRange]) {
      thumb.step = String(this.#step);
    }

    minRange.addEventListener('input', this.#onMinRangeInput);
    maxRange.addEventListener('input', this.#onMaxRangeInput);
    minRange.addEventListener('change', this.#onRangeChange);
    maxRange.addEventListener('change', this.#onRangeChange);

    if (minInput) minInput.addEventListener('input', this.#onMinInputType);
    if (maxInput) maxInput.addEventListener('input', this.#onMaxInputType);

    this.#paintTrack(Number(minRange.value), Number(maxRange.value));
  }

  /**
   * Clamp min thumb so it never crosses max - gap, then sync paired text input.
   */
  #onMinRangeInput = () => {
    const { minRange, maxRange, minInput } = this.refs;
    if (!minRange || !maxRange) return;

    const maxValue = Number(maxRange.value);
    let value = Number(minRange.value);
    const ceiling = maxValue - this.#gap;
    if (value > ceiling) {
      value = ceiling;
      minRange.value = String(value);
    }

    if (minInput) minInput.value = value > 0 ? String(value) : '';
    this.#paintTrack(value, maxValue);
  };

  /**
   * Clamp max thumb so it never crosses min + gap, then sync paired text input.
   */
  #onMaxRangeInput = () => {
    const { minRange, maxRange, maxInput } = this.refs;
    if (!minRange || !maxRange) return;

    const minValue = Number(minRange.value);
    let value = Number(maxRange.value);
    const floor = minValue + this.#gap;
    if (value < floor) {
      value = floor;
      maxRange.value = String(value);
    }

    if (maxInput) {
      maxInput.value = value < this.#rangeMax ? String(value) : '';
    }
    this.#paintTrack(minValue, value);
  };

  /**
   * Drag release: trigger the same AJAX flow the text inputs use today.
   */
  #onRangeChange = () => {
    this.updatePriceFilterAndResults();
  };

  /**
   * Typing in the min text input moves the min thumb.
   */
  #onMinInputType = () => {
    const { minInput, minRange, maxRange } = this.refs;
    if (!minInput || !minRange || !maxRange) return;

    const parsed = this.#parseTextValue(minInput.value);
    if (parsed === null) return;

    const maxValue = Number(maxRange.value);
    const clamped = Math.min(Math.max(parsed, this.#rangeMin), maxValue - this.#gap);
    minRange.value = String(clamped);
    this.#paintTrack(clamped, maxValue);
  };

  /**
   * Typing in the max text input moves the max thumb.
   */
  #onMaxInputType = () => {
    const { maxInput, minRange, maxRange } = this.refs;
    if (!maxInput || !minRange || !maxRange) return;

    const parsed = this.#parseTextValue(maxInput.value);
    const minValue = Number(minRange.value);
    // Empty max input means "up to range max".
    const target = parsed === null ? this.#rangeMax : parsed;
    const clamped = Math.max(Math.min(target, this.#rangeMax), minValue + this.#gap);
    maxRange.value = String(clamped);
    this.#paintTrack(minValue, clamped);
  };

  /**
   * Best-effort parser for the formatted money text inputs.
   * Returns null when the field is empty or unparseable.
   * @param {string} raw
   * @returns {number | null}
   */
  #parseTextValue(raw) {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    // Strip thousands separators; keep the last separator as the decimal mark.
    const normalized = trimmed.replace(/[^0-9.,]/g, '').replace(/,(?=\d{3}\b)/g, '');
    const parsed = Number(normalized.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Updates the gradient track CSS variables.
   * @param {number} minValue
   * @param {number} maxValue
   */
  #paintTrack(minValue, maxValue) {
    const { sliderWrapper } = this.refs;
    if (!sliderWrapper) return;
    sliderWrapper.style.setProperty('--price-facet-slider-lower', String(minValue));
    sliderWrapper.style.setProperty('--price-facet-slider-upper', String(maxValue));
    sliderWrapper.style.setProperty('--price-facet-slider-range', String(this.#rangeMax - this.#rangeMin || 1));
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const details = event.target.closest('details');

    if (details?.hasAttribute('data-dimension-filter')) {
      for (const facet of details.querySelectorAll('dimension-facet-component')) {
        facet.reset();
      }
    } else {
      const container = event.target.closest('facet-inputs-component, price-facet-component');
      container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.value = '';
        }
      });

      const statusComponent = details?.querySelector('facet-status-component');
      if (statusComponent instanceof FacetStatusComponent) {
        statusComponent.clearSummary();
      }
    }

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // Disable the sort control that didn't fire this change so only one sort_by submits (composedPath = real target; event.target is proxied to the component).
    const origin = event.composedPath?.()[0] ?? event.target;
    const changedSelect = origin instanceof HTMLSelectElement;
    const selectElement = this.querySelector('select[name="sort_by"]');
    const radioInputs = this.querySelectorAll('input[name="sort_by"]');

    /** @type {(HTMLInputElement | HTMLSelectElement)[]} */
    const disabledForSubmit = [];

    if (changedSelect) {
      radioInputs.forEach((input) => {
        if (input instanceof HTMLInputElement && !input.disabled) {
          input.disabled = true;
          disabledForSubmit.push(input);
        }
      });
    } else if (selectElement instanceof HTMLSelectElement && !selectElement.disabled) {
      selectElement.disabled = true;
      disabledForSubmit.push(selectElement);
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable only the control we disabled for the submission.
    disabledForSubmit.forEach((element) => {
      element.disabled = false;
    });

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? (event.target.dataset.optionName ?? '') : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Parses a bucket label like "10-20cm" or "5 - 15 in" into a numeric range.
 * Strips unit suffixes (cm, in) and splits on hyphen. Returns {0, 0} if
 * the label doesn't match the expected "X-Y" format.
 *
 * @param {string} label - The bucket label (e.g. "10-20cm", "5 - 15 in")
 * @returns {{ min: number, max: number }}
 */
function parseBucketLabel(label) {
  const removedUnit = label.replace('cm', '').replace('in', '').trim();
  const parts = removedUnit.split('-').map((part) => part.trim());
  if (parts.length === 2) return { min: Number(parts[0]), max: Number(parts[1]) };
  return { min: 0, max: 0 };
}

/**
 * @typedef {Object} DimensionFacetRefs
 * @property {HTMLInputElement} minSlider - The min range slider
 * @property {HTMLInputElement} maxSlider - The max range slider
 * @property {HTMLElement} rangeTrack - The highlighted range track
 * @property {HTMLElement} minDisplay - The min value display
 * @property {HTMLElement} maxDisplay - The max value display
 * @property {HTMLInputElement[]} bucketInputs - Hidden bucket checkboxes
 */

/**
 * Handles dimension facet slider functionality.
 * Maps dual-handle range sliders to bucketed checkbox filters.
 *
 * @extends {Component<DimensionFacetRefs>}
 */
class DimensionFacetComponent extends Component {
  /** @type {{ min: number, max: number, input: HTMLInputElement }[]} */
  #buckets = [];

  /** @type {number[]} */
  #boundaries = [];

  connectedCallback() {
    super.connectedCallback();
    this.#parseBuckets();
    this.#initSliders();

    if (this.hasAttribute('data-disabled')) {
      this.reset();
      return;
    }

    this.#syncFromCheckboxes();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#parseBuckets();
    this.#initSliders();

    if (this.hasAttribute('data-disabled')) {
      this.reset();
      return;
    }

    this.#syncFromCheckboxes();
  }

  /**
   * Resets the slider to full range with no active filters
   */
  reset() {
    const { minSlider, maxSlider } = this.refs;
    if (!minSlider || !maxSlider) return;

    for (const bucket of this.#buckets) {
      bucket.input.checked = false;
    }

    minSlider.value = '0';
    maxSlider.value = String(this.#boundaries.length - 1);
    this.#updateUI();
  }

  /**
   * Parses bucket labels from hidden checkboxes to extract numeric boundaries
   */
  #parseBuckets() {
    const { bucketInputs } = this.refs;
    if (!bucketInputs) return;

    this.#buckets = bucketInputs.map((input) => ({
      ...parseBucketLabel(input.dataset.bucketLabel || ''),
      input,
    }));

    const boundarySet = new Set([0]);

    for (const bucket of this.#buckets) {
      boundarySet.add(bucket.max);
    }

    const sorted = [...this.#buckets].sort((a, b) => a.min - b.min);
    for (let i = 0; i < sorted.length; i++) {
      const prevMax = i > 0 ? sorted[i - 1].max : -1;
      if (sorted[i].min > prevMax + 1) {
        boundarySet.add(sorted[i].min - 1);
      }
    }

    this.#boundaries = [...boundarySet].sort((a, b) => a - b);
  }

  /**
   * Initializes slider min/max/step attributes based on boundaries
   */
  #initSliders() {
    const { minSlider, maxSlider } = this.refs;
    if (!minSlider || !maxSlider || this.#boundaries.length === 0) return;

    const maxIndex = this.#boundaries.length - 1;

    minSlider.min = '0';
    minSlider.max = String(maxIndex);
    minSlider.step = '1';

    maxSlider.min = '0';
    maxSlider.max = String(maxIndex);
    maxSlider.step = '1';
  }

  /**
   * Syncs slider positions from currently checked checkboxes (initial state)
   */
  #syncFromCheckboxes() {
    const { minSlider, maxSlider } = this.refs;
    if (!minSlider || !maxSlider || this.#boundaries.length === 0) return;

    const checkedBuckets = this.#buckets.filter((b) => b.input.checked);

    if (checkedBuckets.length === 0) {
      minSlider.value = '0';
      maxSlider.value = String(this.#boundaries.length - 1);
    } else {
      const minVal = Math.min(...checkedBuckets.map((b) => b.min));
      const maxVal = Math.max(...checkedBuckets.map((b) => b.max));

      minSlider.value = String(this.#boundaryToIndex(minVal));
      maxSlider.value = String(this.#boundaryToIndex(maxVal));
    }

    this.#updateUI();
  }

  /**
   * Finds the closest boundary index for a given value
   * @param {number} value
   * @returns {number}
   */
  #boundaryToIndex(value) {
    let closestIndex = 0;
    let closestDist = Infinity;
    for (let i = 0; i < this.#boundaries.length; i++) {
      const dist = Math.abs((this.#boundaries[i] ?? 0) - value);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    }
    return closestIndex;
  }

  /**
   * Handles slider input event (dragging)
   */
  handleSliderInput() {
    if (this.hasAttribute('data-disabled')) return;

    const { minSlider, maxSlider } = this.refs;
    if (!minSlider || !maxSlider) return;

    let minIdx = parseInt(minSlider.value, 10);
    let maxIdx = parseInt(maxSlider.value, 10);

    if (minIdx > maxIdx) {
      const temp = minIdx;
      minIdx = maxIdx;
      maxIdx = temp;
      minSlider.value = String(minIdx);
      maxSlider.value = String(maxIdx);
    }

    this.#updateCheckboxes(minIdx, maxIdx);
    this.#updateUI(minIdx, maxIdx);
  }

  /**
   * Handles slider change event (release) — triggers filter update
   */
  handleSliderChange() {
    if (this.hasAttribute('data-disabled')) return;
    this.handleSliderInput();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Updates hidden checkboxes based on which buckets overlap the slider range
   * @param {number} minIdx
   * @param {number} maxIdx
   */
  #updateCheckboxes(minIdx, maxIdx) {
    const sliderMin = this.#boundaries[minIdx] ?? 0;
    const sliderMax = this.#boundaries[maxIdx] ?? 0;
    const isFullRange = minIdx === 0 && maxIdx === this.#boundaries.length - 1;

    for (const bucket of this.#buckets) {
      const overlaps = bucket.min < sliderMax && bucket.max > sliderMin;
      bucket.input.checked = !isFullRange && overlaps;
    }
  }

  /**
   * Updates display labels and range track
   * @param {number} [minIdx] - Pre-computed min index
   * @param {number} [maxIdx] - Pre-computed max index
   */
  #updateUI(minIdx, maxIdx) {
    const { minSlider, maxSlider, minDisplay, maxDisplay, rangeTrack } = this.refs;
    if (!minSlider || !maxSlider) return;

    if (minIdx === undefined || maxIdx === undefined) {
      minIdx = Math.min(parseInt(minSlider.value, 10), parseInt(maxSlider.value, 10));
      maxIdx = Math.max(parseInt(minSlider.value, 10), parseInt(maxSlider.value, 10));
    }

    const minVal = this.#boundaries[minIdx] ?? 0;
    const maxVal = this.#boundaries[maxIdx] ?? 0;

    if (minDisplay && maxDisplay) {
      minDisplay.textContent = `${minVal}`;

      maxDisplay.textContent = `${maxVal}`;
    }

    minSlider.setAttribute('aria-valuetext', `${minVal}`);
    maxSlider.setAttribute('aria-valuetext', `${maxVal}`);

    if (rangeTrack) {
      const max = this.#boundaries.length - 1;
      if (max > 0) {
        rangeTrack.style.left = `${(minIdx / max) * 100}%`;
        rangeTrack.style.right = `${((max - maxIdx) / max) * 100}%`;
      }
    }
  }
}

if (!customElements.get('dimension-facet-component')) {
  customElements.define('dimension-facet-component', DimensionFacetComponent);
}

/**
 * @typedef {Object} DimensionUnitToggleRefs
 * @property {HTMLButtonElement} cmBtn - The cm button
 * @property {HTMLButtonElement} inBtn - The in button
 */

/**
 * Handles switching between cm and in dimension sliders.
 * Shows/hides the appropriate slider group and clears the hidden one's checkboxes.
 *
 * @extends {Component<DimensionUnitToggleRefs>}
 */
class DimensionUnitToggleComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#restoreUnit();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreUnit();
  }

  /**
   * Restores the active unit from JS state, or detects it from server-rendered HTML on first load
   */
  #restoreUnit() {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    if (facetsForm.activeUnit) {
      this.#applyUnit(facetsForm.activeUnit);
      return;
    }

    // First load: detect unit from the server-rendered data-unit attribute
    const serverUnit = this.dataset.unit || 'cm';
    facetsForm.activeUnit = serverUnit;
    this.#applyUnit(serverUnit);
  }

  /**
   * Switches between cm and in slider groups
   * @param {Event} event - The click event
   */
  switchUnit(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const unit = event.target.dataset.unit;
    if (!unit) return;

    const { cmBtn, inBtn, cmSliders, inSliders } = this.refs;
    if (!cmBtn || !inBtn) return;
    if (!(cmSliders instanceof HTMLElement) || !(inSliders instanceof HTMLElement)) return;

    this.#applyUnit(unit);

    const hiddenGroup = unit === 'cm' ? inSliders : cmSliders;
    const hadChecked = hiddenGroup.querySelectorAll('input[type="checkbox"]:checked').length > 0;

    this.#clearCheckboxes(hiddenGroup);

    // Reset sliders and range tracks in the newly hidden group
    for (const facet of hiddenGroup.querySelectorAll('dimension-facet-component')) {
      facet.reset();
    }

    // Store unit on parent form so it survives morph
    const facetsForm = this.closest('facets-form-component');
    if (facetsForm instanceof FacetsFormComponent) {
      facetsForm.activeUnit = unit;

      // Only trigger filter update if checkboxes were actually cleared
      if (hadChecked) {
        facetsForm.updateFilters();
      }
    }
  }

  /**
   * Applies the given unit to the toggle UI
   * @param {string} unit - 'cm' or 'in'
   */
  #applyUnit(unit) {
    const { cmBtn, inBtn } = this.refs;
    if (!cmBtn || !inBtn) return;

    this.dataset.unit = unit;
    cmBtn.classList.toggle('dimension-unit-toggle__btn--active', unit === 'cm');
    inBtn.classList.toggle('dimension-unit-toggle__btn--active', unit === 'in');
  }

  /**
   * Unchecks all hidden checkboxes in a slider group
   * @param {Element} group
   */
  #clearCheckboxes(group) {
    for (const input of group.querySelectorAll('input[type="checkbox"]')) {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
      }
    }
  }
}

if (!customElements.get('dimension-unit-toggle-component')) {
  customElements.define('dimension-unit-toggle-component', DimensionUnitToggleComponent);
}

/**
 * Toggles the vertical filter sidebar between expanded and collapsed states.
 *
 * Canonical state lives in `data-sidebar-state` (`expanded` | `collapsed`) on the toggle
 * button. CSS reacts via `:has(.filters-sidebar-toggle__button[data-sidebar-state='collapsed'])`
 * on `.collection-wrapper`. `aria-expanded` mirrors the state for screen readers.
 *
 *
 * @typedef {Object} FiltersSidebarToggleRefs
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<FiltersSidebarToggleRefs>}
 */
class FiltersSidebarToggle extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.button.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.button?.removeEventListener('click', this.#onClick);
  }

  /**
   * Re-sync `aria-expanded` to `data-sidebar-state` after AJAX morphs. The canonical
   * `data-sidebar-state` survives via morph.js's preserve list; `aria-expanded` does not
   */
  updatedCallback() {
    const button = this.refs.button;
    if (!button) return;
    const expanded = button.getAttribute('data-sidebar-state') === 'expanded';
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  #onClick = () => {
    const button = this.refs.button;
    const next = button.getAttribute('data-sidebar-state') === 'expanded' ? 'collapsed' : 'expanded';
    startViewTransition(() => {
      button.setAttribute('data-sidebar-state', next);
      button.setAttribute('aria-expanded', next === 'expanded' ? 'true' : 'false');
    }, ['product-grid']);
  };
}

if (!customElements.get('filters-sidebar-toggle')) {
  customElements.define('filters-sidebar-toggle', FiltersSidebarToggle);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
